import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// Track how many dialog portals are currently mounted so nested dialogs
// can avoid stealing focus from each other.
let openDialogCount = 0;

/**
 * Returns true if a dialog is mounted on top of this one (nested stack).
 * Used by parent dialogs to decide whether click-outside should close them.
 */
export const isAnyDialogNested = () => openDialogCount > 1;
export const getOpenDialogCount = () => openDialogCount;

// Jun 2026 — the billing directory edge panel ([data-billing-dir-root])
// must stay fully interactive while ANY modal layer is open.  Radix
// treats clicks/focus inside it as "outside" interactions and would
// dismiss whichever dialog/sheet is open behind it, so every outside-
// interaction handler whitelists it globally here.
export const isBillingDirInteraction = (e) => {
  const t = e?.detail?.originalEvent?.target ?? e?.target;
  return !!t?.closest?.('[data-billing-dir-root]');
};

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Custom overlay fallback for non-modal dialogs.
 * Radix's DialogOverlay returns null when modal={false}, so we render
 * our own backdrop to maintain the visual overlay.
 */
const NonModalOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 animate-in fade-in-0",
      className
    )}
    {...props} />
))
NonModalOverlay.displayName = "NonModalOverlay"

/**
 * DialogContext to pass modal state from Dialog root to DialogContent.
 * We use this to detect whether the parent <Dialog> is modal={false}
 * so DialogContent can render the appropriate overlay.
 */
const DialogModalContext = React.createContext({ isModal: true, onOpenChange: null, allowOverlayClose: true });

/**
 * Enhanced Dialog wrapper that exposes modal state to children via context.
 */
const EnhancedDialog = React.forwardRef(({ modal = true, children, onOpenChange, allowOverlayClose = true, ...props }, ref) => {
  const ctx = React.useMemo(() => ({ isModal: modal, onOpenChange, allowOverlayClose }), [modal, onOpenChange, allowOverlayClose]);
  return (
    <DialogModalContext.Provider value={ctx}>
      <DialogPrimitive.Root modal={modal} onOpenChange={onOpenChange} {...props}>
        {children}
      </DialogPrimitive.Root>
    </DialogModalContext.Provider>
  );
});
EnhancedDialog.displayName = "Dialog"

const DialogContent = React.forwardRef(({ className, children, hideCloseButton = false, overlayClassName, onOpenAutoFocus, onPointerDownOutside, onInteractOutside, ...props }, ref) => {
  const { isModal, onOpenChange, allowOverlayClose } = React.useContext(DialogModalContext);
  // Track whether this dialog mounted while another was already open
  const isNestedRef = React.useRef(false);

  React.useLayoutEffect(() => {
    isNestedRef.current = openDialogCount > 0;
    openDialogCount++;
    return () => { openDialogCount--; };
  }, []);

  const handleOpenAutoFocus = React.useCallback((e) => {
    if (onOpenAutoFocus) {
      onOpenAutoFocus(e);
      return;
    }
    if (isNestedRef.current) {
      // Nested dialog: don't steal focus from existing inputs
      e.preventDefault();
    }
  }, [onOpenAutoFocus]);

  const handlePointerDownOutside = React.useCallback((e) => {
    if (isBillingDirInteraction(e)) {
      e.preventDefault();
      return;
    }
    if (onPointerDownOutside) {
      onPointerDownOutside(e);
      return;
    }
    if (openDialogCount > 1) {
      e.preventDefault();
    }
  }, [onPointerDownOutside]);

  const handleInteractOutside = React.useCallback((e) => {
    if (isBillingDirInteraction(e)) {
      e.preventDefault();
      return;
    }
    if (onInteractOutside) {
      onInteractOutside(e);
      return;
    }
    if (openDialogCount > 1) {
      e.preventDefault();
    }
  }, [onInteractOutside]);

  return (
    <DialogPortal>
      {/* Radix renders its own overlay only when modal={true}.
          For non-modal dialogs we render a manual backdrop. */}
      {isModal ? (
        <DialogOverlay className={overlayClassName} />
      ) : (
        <NonModalOverlay
          className={overlayClassName}
          onClick={() => {
            if (!allowOverlayClose) return;
            if (onOpenChange) {
              onOpenChange(false);
            }
          }}
        />
      )}
      <DialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={handleOpenAutoFocus}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg pointer-events-auto",
          className
        )}
        {...props}>
        {!hideCloseButton && (
          <DialogPrimitive.Close
            data-testid="dialog-close-btn"
            aria-label="Close"
            className="absolute right-3 top-3 z-[60] rounded-full bg-white dark:bg-gray-800 shadow-md p-1.5 opacity-90 ring-offset-background transition-all hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none border border-gray-200">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  EnhancedDialog as Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
